import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin, Lock, Ticket, X as XIcon, MagnifyingGlass } from '@phosphor-icons/react';
import API from '../lib/api';
import { useAuth } from '../lib/auth';

// Fix leaflet default icon paths
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const DEFAULT_CENTER = [12.9716, 77.5946]; // Bangalore

const DraggableMarker = ({ position, setPosition }) => {
  const markerRef = useRef(null);
  useMapEvents({
    click(e) { setPosition([e.latlng.lat, e.latlng.lng]); },
  });
  return (
    <Marker
      draggable
      eventHandlers={{
        dragend() {
          const m = markerRef.current;
          if (m) { const p = m.getLatLng(); setPosition([p.lat, p.lng]); }
        },
      }}
      ref={markerRef}
      position={position}
    />
  );
};

const Recenter = ({ position }) => {
  const map = useMap();
  useEffect(() => { map.setView(position, map.getZoom()); }, [position, map]);
  return null;
};

const Checkout = () => {
  const nav = useNavigate();
  const { user } = useAuth();
  const [cart, setCart] = useState([]);
  const sanitizePhone = (p) => String(p || '').replace(/\D/g, '').slice(-10);
  const [form, setForm] = useState({ address: '', pincode: '', phone: sanitizePhone(user?.phone) });
  const [pos, setPos] = useState(DEFAULT_CENTER);
  const [busy, setBusy] = useState(false);
  const [payCfg, setPayCfg] = useState(null);
  const [phoneBlink, setPhoneBlink] = useState(true);
  // Coupon state
  const [couponInput, setCouponInput] = useState('');
  const [applied, setApplied] = useState(null);       // {code, description, discount, discount_type, discount_value}
  const [couponBusy, setCouponBusy] = useState(false);
  const [autoSuggest, setAutoSuggest] = useState(null); // best auto-apply suggestion
  // Address autocomplete (Nominatim / OpenStreetMap — no API key needed)
  const [placeQuery, setPlaceQuery] = useState('');
  const [placeResults, setPlaceResults] = useState([]);
  const [placeSearching, setPlaceSearching] = useState(false);
  const [showPlaceDropdown, setShowPlaceDropdown] = useState(false);
  const placeDebounce = useRef(null);
  // Address book — last 3 unique delivered-to addresses for one-tap reuse
  const [savedAddresses, setSavedAddresses] = useState([]);

  useEffect(() => {
    // Auto-fill phone from profile — strip country-code prefix so seeded '+919...' values
    // still satisfy the strict 10-digit regex without the user having to edit.
    if (user?.phone && !form.phone) setForm(f => ({ ...f, phone: sanitizePhone(user.phone) }));
    const t = setTimeout(() => setPhoneBlink(false), 6000);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [user?.phone]);

  useEffect(() => {
    const raw = sessionStorage.getItem('pk_cart');
    if (!raw) { nav('/'); return; }
    setCart(JSON.parse(raw));
    API.get('/payment/config').then(r => setPayCfg(r.data)).catch(() => setPayCfg({ configured: false }));
    // Fetch the customer's last 3 delivered-to addresses (one-tap reuse)
    API.get('/orders/addresses').then(r => setSavedAddresses(r.data?.addresses || [])).catch(() => setSavedAddresses([]));
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (p) => setPos([p.coords.latitude, p.coords.longitude]),
        () => {},
        { enableHighAccuracy: false, timeout: 5000 }
      );
    }
  }, [nav]);

  const subtotal = cart.reduce((s, i) => s + (i.total || 0), 0);
  const discount = applied?.discount || 0;
  const total = Math.max(0, subtotal - discount);
  const mapLink = `https://www.google.com/maps/search/?api=1&query=${pos[0]},${pos[1]}`;

  const cartProductIds = cart.map(i => i.product_id).filter(Boolean);
  const cartCategoryIds = Array.from(new Set(cart.map(i => i.category_id).filter(Boolean)));

  const applyCoupon = async (code) => {
    const c = (code || couponInput || '').trim().toUpperCase();
    if (!c) { toast.error('Enter a coupon code'); return; }
    setCouponBusy(true);
    try {
      const r = await API.post('/coupons/validate', {
        code: c, subtotal, item_product_ids: cartProductIds, item_category_ids: cartCategoryIds,
      });
      if (!r.data.ok) { toast.error(r.data.reason || 'Coupon not applicable'); setApplied(null); return; }
      setApplied({ ...r.data.coupon, discount: r.data.discount });
      setCouponInput(c);
      toast.success(`Coupon ${c} applied — you save ₹${r.data.discount}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not apply coupon');
    } finally {
      setCouponBusy(false);
    }
  };

  const removeCoupon = () => { setApplied(null); setCouponInput(''); };

  // One-tap reuse of a saved delivered-to address. Fills form, drops pin (if we have coords).
  const applySavedAddress = (a) => {
    if (!a) return;
    const cleanPhone = sanitizePhone(a.phone) || form.phone;
    setForm({ address: a.address || '', pincode: a.pincode || '', phone: cleanPhone });
    setPlaceQuery(a.address || '');
    setShowPlaceDropdown(false);
    if (typeof a.lat === 'number' && typeof a.lng === 'number') {
      // Suppress the reverse-geocode overwrite on the next `pos` change — the address
      // was picked explicitly by the user so we must not stomp on it.
      pinInitRef.current = true;
      setPos([a.lat, a.lng]);
    }
    toast.success('Address filled — verify and pay');
  };

  // Address search — debounced Nominatim query. Results include lat/lng so we drop the pin on select.
  useEffect(() => {
    if (!placeQuery || placeQuery.trim().length < 3) { setPlaceResults([]); return; }
    if (placeDebounce.current) clearTimeout(placeDebounce.current);
    placeDebounce.current = setTimeout(async () => {
      setPlaceSearching(true);
      try {
        const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&q=${encodeURIComponent(placeQuery + ', India')}&addressdetails=1&countrycodes=in&limit=6`;
        const r = await fetch(url, { headers: { 'Accept': 'application/json' } });
        if (r.ok) setPlaceResults(await r.json());
      } catch { /* silent */ }
      finally { setPlaceSearching(false); }
    }, 400);
    return () => placeDebounce.current && clearTimeout(placeDebounce.current);
  }, [placeQuery]);

  const pickPlace = (p) => {
    setPos([parseFloat(p.lat), parseFloat(p.lon)]);
    setPlaceQuery(p.display_name);
    setPlaceResults([]);
    setShowPlaceDropdown(false);
    // Always overwrite address+pincode from picked place so the two stay in sync
    setForm(f => ({
      ...f,
      address: p.display_name,
      pincode: (p.address?.postcode || f.pincode || '').replace(/\D/g, '').slice(0, 6),
    }));
    toast.success('Pin dropped');
  };

  // Reverse-geocode: whenever the user drags/clicks the pin on the map, fetch the address
  // for the new coordinates and mirror it into the search input + address field.
  const reverseGeocode = async ([lat, lng]) => {
    try {
      const r = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
        { headers: { Accept: 'application/json' } }
      );
      if (!r.ok) return;
      const p = await r.json();
      if (!p || !p.display_name) return;
      setPlaceQuery(p.display_name);
      setForm(f => ({
        ...f,
        address: p.display_name,
        pincode: (p.address?.postcode || f.pincode || '').replace(/\D/g, '').slice(0, 6),
      }));
    } catch { /* silent */ }
  };

  // Debounce reverse-geocode when `pos` changes from a drag/click. Skip the very first render.
  const pinInitRef = useRef(true);
  const revDebounceRef = useRef(null);
  useEffect(() => {
    if (pinInitRef.current) { pinInitRef.current = false; return; }
    if (revDebounceRef.current) clearTimeout(revDebounceRef.current);
    revDebounceRef.current = setTimeout(() => reverseGeocode(pos), 500);
    return () => revDebounceRef.current && clearTimeout(revDebounceRef.current);
    // eslint-disable-next-line
  }, [pos[0], pos[1]]);

  // Ask backend if there's an auto-apply-eligible coupon for this cart
  useEffect(() => {
    if (!subtotal || applied) { setAutoSuggest(null); return; }
    let cancel = false;
    API.post('/coupons/auto-apply', {
      subtotal, item_product_ids: cartProductIds, item_category_ids: cartCategoryIds,
    }).then(r => { if (!cancel && r.data.ok) setAutoSuggest(r.data); })
      .catch(() => {});
    return () => { cancel = true; };
    // eslint-disable-next-line
  }, [subtotal, applied]);

  const loadRzpScript = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.address || !form.pincode || !form.phone) { toast.error('Fill all address fields'); return; }
    if (!/^[6-9]\d{9}$/.test(form.phone)) {
      toast.error('Delivery phone must be a valid 10-digit Indian mobile (6-9 start)');
      return;
    }
    setBusy(true);
    try {
      const orderRes = await API.post('/orders', {
        items: cart,
        delivery_address: form.address,
        delivery_pincode: form.pincode,
        delivery_phone: form.phone,
        delivery_map_link: mapLink,
        delivery_lat: pos[0], delivery_lng: pos[1],
        coupon_code: applied?.code || null,
      });
      const orderId = orderRes.data.order_id;

      const rzpRes = await API.post('/payment/create-order', { order_id: orderId });
      const { rzp_order_id, amount, key_id, mock } = rzpRes.data;

      if (mock) {
        // Dev-mock: skip real Razorpay, verify with mock signature
        toast('Dev mode — simulating payment…');
        await new Promise(r => setTimeout(r, 700));
        await API.post('/payment/verify', {
          order_id: orderId, razorpay_order_id: rzp_order_id,
          razorpay_payment_id: `pay_MOCK${Date.now()}`,
          razorpay_signature: `mock_sig_${Date.now()}`,
        });
        sessionStorage.removeItem('pk_cart');
        toast.success('Order placed!');
        nav(`/orders/${orderId}`, { replace: true });
        return;
      }

      await loadRzpScript();
      const rzp = new window.Razorpay({
        key: key_id, amount, currency: 'INR', name: 'PrintingKit',
        order_id: rzp_order_id, prefill: { name: user.name, email: user.email, contact: form.phone },
        theme: { color: '#e30066' },
        handler: async (resp) => {
          try {
            await API.post('/payment/verify', {
              order_id: orderId,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            sessionStorage.removeItem('pk_cart');
            toast.success('Payment verified');
            nav(`/orders/${orderId}`, { replace: true });
          } catch (err) {
            toast.error(err.response?.data?.detail || 'Verification failed');
          }
        },
      });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Checkout failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6" style={{ isolation: 'isolate', zIndex: 0 }}>
      <div className="reg-mark">
        <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: 'var(--pk-primary)' }}>— Checkout</div>
        <h1 className="font-display text-[28px] mt-2 leading-tight">Deliver to.</h1>
      </div>

      <div className="mt-4 pk-card p-4">
        <div className="text-[10px] tracking-[0.22em] uppercase text-neutral-500">Cart</div>
        {cart.map((i, idx) => (
          <div key={idx} className="mt-2 flex items-baseline justify-between text-[13px]">
            <div><b>{i.product_name}</b> <span className="text-neutral-500">× {i.quantity}</span></div>
            <div className="font-mono">₹{i.total.toLocaleString('en-IN')}</div>
          </div>
        ))}

        {/* Coupon input — collapsed & minimal by default. Apply slides in only when user types. */}
        <div className="mt-3 pt-3 border-t border-dashed border-neutral-200">
          {applied ? (
            <div className="flex items-center justify-between gap-2 text-[12px]" data-testid="applied-coupon">
              <div className="flex items-center gap-1.5 min-w-0" style={{ color: 'var(--pk-primary)' }}>
                <Ticket size={12} weight="fill"/>
                <span className="font-mono font-bold tracking-widest">{applied.code}</span>
                <span className="text-neutral-500 truncate">· saves ₹{applied.discount}</span>
              </div>
              <button type="button" onClick={removeCoupon} className="text-neutral-400 hover:text-black shrink-0" data-testid="remove-coupon" aria-label="Remove coupon">
                <XIcon size={12} weight="bold"/>
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <Ticket size={11} weight="fill" className="text-neutral-400 shrink-0" />
                <div className="flex-1 flex items-center gap-2 min-w-0">
                  <input
                    type="text"
                    className="flex-1 min-w-0 bg-transparent border-0 outline-none font-mono tracking-widest uppercase text-[12px] py-1 placeholder:text-neutral-400 placeholder:font-normal placeholder:tracking-normal placeholder:normal-case"
                    placeholder="Have a coupon code?"
                    value={couponInput}
                    onChange={e => setCouponInput(e.target.value.toUpperCase().slice(0, 24))}
                    data-testid="coupon-input"
                  />
                  <button
                    type="button"
                    disabled={couponBusy || !couponInput}
                    onClick={() => applyCoupon()}
                    className={`text-[10px] font-bold tracking-widest uppercase shrink-0 transition-all duration-200 ${couponInput ? 'opacity-100 translate-x-0 max-w-[80px]' : 'opacity-0 translate-x-2 max-w-0 overflow-hidden pointer-events-none'}`}
                    style={{ color: 'var(--pk-primary)' }}
                    data-testid="apply-coupon"
                  >
                    {couponBusy ? '…' : 'Apply →'}
                  </button>
                </div>
              </div>
              {autoSuggest && (
                <button type="button" onClick={() => applyCoupon(autoSuggest.coupon.code)} className="mt-2 text-[10px] font-bold tracking-wider text-neutral-500 hover:text-black underline underline-offset-2" data-testid="auto-apply-suggest">
                  Use <b className="font-mono">{autoSuggest.coupon.code}</b> — save ₹{autoSuggest.discount}
                </button>
              )}
            </>
          )}
        </div>

        <div className="mt-3 pt-2 border-t-2 border-dashed border-neutral-300 space-y-1">
          <div className="flex justify-between text-[12px] text-neutral-600">
            <div>Subtotal</div>
            <div className="font-mono">₹{subtotal.toLocaleString('en-IN')}</div>
          </div>
          {discount > 0 && (
            <div className="flex justify-between text-[12px] font-bold" style={{color: 'var(--pk-primary)'}} data-testid="discount-row">
              <div>Coupon ({applied.code})</div>
              <div className="font-mono">− ₹{discount.toLocaleString('en-IN')}</div>
            </div>
          )}
          {/* Free-delivery progress — under ₹1200, delivery fee charged on actuals; at or above, it's free. */}
          {(() => {
            const FREE_MIN = 1200;
            const amt = total;
            if (amt >= FREE_MIN) {
              return (
                <div className="mt-1.5 pk-card p-2 flex items-center gap-2" data-testid="free-delivery-unlocked" style={{ borderColor: 'var(--pk-primary)', background: 'color-mix(in srgb, var(--pk-primary) 8%, #ffffff)' }}>
                  <div className="w-6 h-6 rounded-full flex items-center justify-center font-bold text-white text-[11px]" style={{ background: 'var(--pk-primary)' }}>✓</div>
                  <div className="flex-1 text-[11px] leading-tight">
                    <div className="font-bold">Free delivery unlocked</div>
                    <div className="text-neutral-500">No delivery charges on this order.</div>
                  </div>
                </div>
              );
            }
            const left = FREE_MIN - amt;
            const pct = Math.max(0, Math.min(100, (amt / FREE_MIN) * 100));
            return (
              <div className="mt-1.5" data-testid="free-delivery-progress">
                <div className="flex items-baseline justify-between text-[11px] mb-1">
                  <div className="font-bold">
                    Add <span className="font-mono" style={{ color: 'var(--pk-primary)' }}>₹{left.toLocaleString('en-IN')}</span> for FREE delivery
                  </div>
                  <div className="font-mono text-neutral-400 text-[10px]">₹1,200+</div>
                </div>
                <div className="h-1.5 w-full bg-neutral-200 rounded-full overflow-hidden">
                  <div className="h-full transition-all duration-500 rounded-full" style={{ width: `${pct}%`, background: 'var(--pk-primary)' }} />
                </div>
                <div className="text-[10px] text-neutral-500 mt-1 leading-snug">
                  Delivery charges on orders under ₹1,200 are added based on actuals and shared before dispatch.
                </div>
              </div>
            );
          })()}
          <div className="flex justify-between items-baseline pt-1">
            <div className="font-bold text-[13px] uppercase tracking-wider">Total</div>
            <div className="font-display text-[22px] font-mono">₹{total.toLocaleString('en-IN')}</div>
          </div>
        </div>
      </div>

      <form onSubmit={submit} className="mt-5 space-y-3">
        {savedAddresses.length > 0 && (
          <div data-testid="saved-addresses">
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-neutral-600 inline-flex items-center gap-1">
                <MapPin size={11} weight="fill" style={{ color: 'var(--pk-primary)' }} /> Deliver again to
              </div>
              <div className="text-[9px] tracking-[0.2em] uppercase text-neutral-400">One-tap</div>
            </div>
            <div className="grid grid-cols-1 gap-1.5">
              {savedAddresses.map((a, idx) => {
                const isActive = (form.address || '').trim() === (a.address || '').trim() && form.pincode === a.pincode;
                return (
                  <button
                    key={`${a.address}-${a.pincode}-${idx}`}
                    type="button"
                    onClick={() => applySavedAddress(a)}
                    data-testid={`saved-address-${idx}`}
                    className={`w-full text-left rounded-xl border-2 p-2.5 transition-all active:scale-[0.99] ${isActive ? 'bg-yellow-50 shadow-sm' : 'bg-white hover:bg-neutral-50'}`}
                    style={{ borderColor: isActive ? 'var(--pk-primary)' : '#e5e5e5' }}
                  >
                    <div className="flex items-start gap-2">
                      <div className="mt-0.5 shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-mono font-bold"
                        style={{ background: isActive ? 'var(--pk-primary)' : '#f4f4f5', color: isActive ? '#fff' : '#525252' }}>
                        {idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[12px] leading-snug line-clamp-2 break-words">{a.address}</div>
                        <div className="mt-0.5 text-[10px] font-mono text-neutral-500 flex items-center gap-1.5">
                          <span>PIN {a.pincode}</span>
                          {a.phone && <><span className="text-neutral-300">·</span><span>{sanitizePhone(a.phone)}</span></>}
                        </div>
                      </div>
                      {isActive && (
                        <div className="shrink-0 text-[9px] tracking-[0.18em] uppercase font-bold" style={{ color: 'var(--pk-primary)' }}>
                          Selected
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="mt-2 flex items-center gap-2">
              <div className="flex-1 h-px bg-neutral-200" />
              <div className="text-[9px] tracking-[0.22em] uppercase text-neutral-400">or enter new</div>
              <div className="flex-1 h-px bg-neutral-200" />
            </div>
          </div>
        )}
        <div>
          <label className="pk-label">Address</label>
          <textarea className="pk-textarea" rows={3} value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} required data-testid="co-address" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="pk-label">Pincode</label>
            <input className="pk-input font-mono" inputMode="numeric" pattern="[0-9]*" maxLength={6} value={form.pincode} onChange={e => setForm({ ...form, pincode: e.target.value.replace(/\D/g, '') })} required data-testid="co-pincode" />
          </div>
          <div>
            <label className="pk-label">Phone</label>
            <input
              className={`pk-input font-mono tracking-widest ${phoneBlink ? 'phone-blink' : ''}`}
              inputMode="numeric"
              pattern="[6-9][0-9]{9}"
              maxLength={10}
              value={form.phone}
              onChange={e => { setPhoneBlink(false); setForm({ ...form, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }); }}
              onFocus={() => setPhoneBlink(false)}
              required
              data-testid="co-phone"
            />
            <div className={`text-[9px] mt-1 tracking-wider ${form.phone.length === 10 && /^[6-9]/.test(form.phone) ? 'text-emerald-600' : phoneBlink ? 'text-[color:var(--pk-primary)] font-bold' : 'text-neutral-500'}`}>
              {phoneBlink && form.phone === user?.phone ? '📌 From your profile — please verify or edit' : `${form.phone.length}/10 digits`}
              {form.phone.length === 10 && !/^[6-9]/.test(form.phone) ? ' — must start with 6-9' : ''}
            </div>
          </div>
        </div>

        <div>
          <label className="pk-label flex items-center gap-1"><MapPin size={12} weight="fill"/> Pin your location (drag or use current)</label>

          {/* Address / landmark search */}
          <div className="relative mb-2" data-testid="place-search-wrap">
            <MagnifyingGlass size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none z-10" />
            <input
              type="text"
              className="pk-input"
              style={{ paddingLeft: 38, paddingRight: placeQuery ? 38 : 14 }}
              placeholder="Search a landmark, area or full address"
              value={placeQuery}
              onFocus={() => setShowPlaceDropdown(true)}
              onBlur={() => setTimeout(() => setShowPlaceDropdown(false), 200)}
              onChange={e => { setPlaceQuery(e.target.value); setShowPlaceDropdown(true); }}
              data-testid="place-search"
            />
            {placeQuery && (
              <button
                type="button"
                onClick={() => { setPlaceQuery(''); setPlaceResults([]); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-black z-10"
                aria-label="Clear"
              >
                <XIcon size={12} weight="bold"/>
              </button>
            )}
            {showPlaceDropdown && (placeQuery.length >= 3) && (placeSearching || placeResults.length > 0) && (
              <div className="absolute top-full left-0 right-0 mt-1 pk-card overflow-hidden z-20 max-h-64 overflow-y-auto" data-testid="place-results">
                {placeSearching && <div className="p-3 text-[11px] text-neutral-500">Searching…</div>}
                {!placeSearching && placeResults.map((p, i) => (
                  <button
                    key={p.place_id || i}
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); pickPlace(p); }}
                    className="w-full text-left px-3 py-2 hover:bg-neutral-100 border-b border-neutral-100 last:border-b-0 flex gap-2 items-start"
                    data-testid={`place-result-${i}`}
                  >
                    <MapPin size={14} weight="fill" className="mt-0.5 shrink-0" style={{ color: 'var(--pk-primary)' }} />
                    <div className="min-w-0 flex-1">
                      <div className="text-[12px] font-bold truncate">{p.name || p.display_name.split(',')[0]}</div>
                      <div className="text-[10px] text-neutral-500 truncate">{p.display_name}</div>
                    </div>
                  </button>
                ))}
                {!placeSearching && placeResults.length === 0 && (
                  <div className="p-3 text-[11px] text-neutral-500">No matches — try a nearby landmark or pincode.</div>
                )}
              </div>
            )}
          </div>

          <div className="flex gap-2 mb-2">
            <button
              type="button"
              onClick={() => {
                if (!navigator.geolocation) { toast.error('Geolocation not supported'); return; }
                toast('Detecting location…');
                navigator.geolocation.getCurrentPosition(
                  (p) => { setPos([p.coords.latitude, p.coords.longitude]); toast.success('Location set'); },
                  (err) => toast.error(err.message || 'Location denied'),
                  { enableHighAccuracy: true, timeout: 8000 }
                );
              }}
              className="pk-chip"
              data-testid="use-current-location"
            >
              <MapPin size={12} weight="fill" /> Use my current location
            </button>
          </div>
          <div className="pk-border" style={{ height: 200, position: 'relative', overflow: 'hidden' }} data-testid="pin-map">
            <MapContainer center={pos} zoom={14} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
              <TileLayer
                attribution="&copy; OpenStreetMap"
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <DraggableMarker position={pos} setPosition={setPos} />
              <Recenter position={pos} />
            </MapContainer>
          </div>
          <div className="text-[10px] font-mono mt-1 text-neutral-500">{pos[0].toFixed(5)}, {pos[1].toFixed(5)}</div>
        </div>

        <div className="pk-card p-3 flex items-center gap-2 text-[11px] text-neutral-600">
          <Lock size={14} weight="fill" />
          {payCfg?.configured
            ? <>Secure payment via Razorpay {payCfg.live_mode ? <span className="pk-highlight-magenta">LIVE</span> : <span className="pk-highlight">TEST</span>}</>
            : <>Payment in <span className="pk-highlight">DEV MOCK</span> — no real charges</>}
        </div>

        <button type="submit" disabled={busy} className="pk-btn w-full" data-testid="co-submit">
          {busy ? 'Processing…' : `Pay ₹${total.toLocaleString('en-IN')}`}
        </button>
      </form>
    </div>
  );
};

export default Checkout;
